import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client.js';
import { useSession } from '../../api/session.jsx';
import { ouvreDocument } from '../../api/documents.js';
import { envoisApi, maSignatureApi, piecesApi } from '../../api/resources';
import { BoutonContacts } from '../../components/BoutonContacts.jsx';
import { BoutonMaSignature, estUneImage } from '../../components/MaSignature.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { SignPad } from '../../components/SignPad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { SOC } from '../../constants/company';
import { collaborateursEnPoste, coordonneesDuCollaborateur } from '../../lib/personnel';
import { printDoc } from '../../lib/print';
import { useBoiteEntreprise } from '../../lib/boite-entreprise';
import { tr } from '../../lib/i18n';
import { ordreDeSaisie, today } from '../../lib/utils';
import { tamponVoulu } from '../../lib/tampon';
import { C, DA } from '../../theme/palette';
import { PhotosDecharge } from './PhotosDecharge.jsx';

export function DechargePage({ onNav, soc, clients, employes, estMixte, univers, setUnivers }) {
  const estMag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  // Le bureau signe pour l'entreprise de son propre nom, jamais de celui du
  // patron : « chacun a ses responsabilités ». Le serveur l'inscrit aussi.
  const { session } = useSession();
  const signataireBureau =
    session?.role && session.role !== 'patron' && session.nom
      ? session.nom +
        ' (' +
        ({ secretaire: 'Secrétariat', technicien: 'Technique', comptable: 'Comptabilité' }[
          session.role
        ] || '') +
        ')'
      : '';
  const [view, setView] = useState('form');
  const [f, setF] = useState({
    clientId: ordreDeSaisie(clients)[0]?.id || '',
    objet: '',
    lieu: '',
    destManuel: clients.length === 0,
    destNom: '',
    destAdr: '',
    destNpa: '',
    destTel: '',
    destEmail: '',
    // Le texte proposé, dans la langue de l'entreprise : saisi dans le champ,
    // il partait en français sur la décharge d'une entreprise d'ailleurs.
    // Au magasin, il n'y a pas de travaux : la décharge porte sur la
    // marchandise ou la prestation.
    texte: estMag
      ? tr(
          "Le client soussigné décharge l'entreprise de toute responsabilité concernant la marchandise ou la prestation décrite ci-dessus, ayant été informé des risques et donnant son accord exprès.",
        )
      : tr(
          "Le client soussigné décharge l'entreprise de toute responsabilité concernant les travaux décrits ci-dessus, ayant été informé des risques et donnant son accord exprès pour leur exécution.",
        ),
  });
  const [sig, setSig] = useState('');
  const [sigSoc, setSigSoc] = useState('');
  // Chacun signe pour l'entreprise de sa propre signature enregistrée : le
  // patron la sienne (ou celle de Réglages → Documents), le bureau la sienne.
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => maSignatureApi.lit())
      .then((r) => {
        const image = r?.signature || (signataireBureau ? '' : soc?.sigPatron || '');
        if (vivant && image) setSigSoc((v) => v || image);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [signataireBureau, soc?.sigPatron]);
  // Photos retenues pendant la saisie ; elles partent au stockage avec la
  // décharge, une fois celle-ci enregistrée.
  const [photos, setPhotos] = useState([]);
  const [photosFigees, setPhotosFigees] = useState(false);
  // Photos déjà déposées : un envoi interrompu se reprend sans doublon.
  const photosEnvoyees = useRef(new Set());
  // Un collaborateur parti ne signe plus rien au nom de l'entreprise.
  const collaborateurs = collaborateursEnPoste(employes);
  // Contact du carnet, fiche vCard ou collaborateur : le destinataire se
  // remplit de la même façon, d'où que vienne la personne.
  const remplitDestinataire = (c) =>
    setF((p) => ({
      ...p,
      destNom: c.nom,
      destTel: c.tel,
      destEmail: c.email,
      destAdr: c.adr,
      destNpa: c.npa,
    }));
  // Identifiant de la décharge une fois enregistrée : la seconde demande rend
  // alors la pièce déjà figée, au lieu d'en établir une nouvelle.
  const [dechargeId, setDechargeId] = useState(null);
  const [feuilleEnvoi, setFeuilleEnvoi] = useState(false);
  const [emailEnvoi, setEmailEnvoi] = useState('');
  const [action, setAction] = useState({ etat: '', message: '' });
  const boite = useBoiteEntreprise();
  const cl = clients.find((c) => c.id === f.clientId);
  const dest = f.destManuel
    ? { nom: f.destNom, adr: f.destAdr, npa: f.destNpa, tel: f.destTel, email: f.destEmail }
    : cl;

  // La décharge s'enregistre au premier PDF ou au premier envoi, avec ses
  // photos : le serveur la fige à cette occasion, et une photo arrivée après
  // n'y figurerait pas.
  const enregistreDecharge = async () => {
    let id = dechargeId;
    if (!id) {
      const enregistree = await api.post('/decharges', {
        dateDocument: new Date().toISOString().slice(0, 10),
        clientId: f.destManuel ? null : f.clientId || null,
        destinataireNom: dest?.nom || '',
        destinataireAdresse: dest?.adr || null,
        destinataireNpa: dest?.npa || null,
        destinataireTelephone: dest?.tel || null,
        destinataireEmail: dest?.email || null,
        objet: f.objet || null,
        lieu: f.lieu || null,
        texte: f.texte,
        signatureClient: sig || null,
        signatureEntreprise: sigSoc || null,
      });
      id = enregistree.id;
      setDechargeId(id);
    }
    // Les photos rejoignent la décharge avant que le PDF ne
    // soit demandé : le serveur la fige à cette occasion, et
    // une photo arrivée après n'y figurerait pas.
    for (const photo of photos) {
      if (photosEnvoyees.current.has(photo.cle)) continue;
      await piecesApi.depose('DECHARGE', id, photo.fichier, photo.nom);
      photosEnvoyees.current.add(photo.cle);
    }
    setPhotosFigees(true);
    return id;
  };
  const msgEnvoi =
    'Bonjour,\n\nVeuillez trouver la décharge de ' +
    (soc?.nom || SOC.nom) +
    (f.objet ? ' concernant : ' + f.objet : '') +
    ".\nMerci d'en prendre connaissance et de la signer.\n\nCordialement,\n" +
    (soc?.nom || SOC.nom);
  const emailValide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEnvoi.trim());
  const telWhatsApp = (dest?.tel || '').replace(/[^0-9]/g, '').replace(/^0/, '41');
  const ouvrePdf = async () => {
    setAction({ etat: 'pdf', message: '' });
    try {
      const id = await enregistreDecharge();
      await ouvreDocument(`/decharges/${id}/document`);
      setAction({ etat: '', message: '' });
    } catch (e) {
      setAction({ etat: 'echec', message: e?.message || 'Le PDF n’a pas pu être produit.' });
    }
  };
  // Pas de décharge imprimée sans trace : elle s'enregistre d'abord.
  const imprime = async () => {
    try {
      await enregistreDecharge();
    } catch (e) {
      setAction({
        etat: 'echec',
        message: e?.message || 'La décharge n’a pas pu être enregistrée.',
      });
      return;
    }
    printDoc();
  };
  // L'e-mail part de SwissTech, PDF joint : de la boîte de l'entreprise
  // (info@bektech.ch) quand elle est raccordée, au nom de qui l'envoie.
  const envoieParEmail = async () => {
    setFeuilleEnvoi(false);
    setAction({ etat: 'envoi', message: '' });
    try {
      const id = await enregistreDecharge();
      const r = await envoisApi.envoieDecharge(id, dest?.email ? undefined : emailEnvoi.trim());
      setAction({
        etat: 'ok',
        message: 'Envoyé à ' + (r?.destinataire || dest?.email || emailEnvoi) + ', PDF joint.',
      });
    } catch (e) {
      setAction({ etat: 'echec', message: e?.message || 'La décharge n’a pas pu être envoyée.' });
    }
  };

  if (view === 'doc')
    return (
      <div
        style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#5b5f66' }}
      >
        <style>
          {
            '@media print{ body *{visibility:hidden} .dch,.dch *{visibility:visible} .dch{position:absolute;left:0;top:0;width:100%} .dchnp{display:none!important} }'
          }
        </style>
        <div
          className="dchnp"
          style={{
            background: C.navy,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <button
            onClick={() => setView('form')}
            aria-label="Retour"
            style={{
              background: 'rgba(255,255,255,.12)',
              border: 'none',
              color: '#fff',
              width: 36,
              height: 36,
              borderRadius: 10,
              fontSize: 18,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ←
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>Décharge</div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.7)' }}>Aperçu avant envoi</div>
          </div>
        </div>
        <div
          className="dchnp"
          style={{
            background: '#fff',
            borderBottom: '1px solid ' + C.border,
            padding: '10px 12px',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              {
                cle: 'envoyer',
                icone: '📨',
                label: action.etat === 'envoi' ? 'Envoi…' : 'Envoyer',
                onClick: () => setFeuilleEnvoi(true),
                primaire: true,
              },
              {
                cle: 'pdf',
                icone: '📄',
                label: action.etat === 'pdf' ? 'Préparation…' : 'PDF',
                onClick: ouvrePdf,
              },
              { cle: 'imprimer', icone: '🖨', label: 'Imprimer', onClick: imprime },
            ].map((b) => (
              <button
                key={b.cle}
                type="button"
                onClick={action.etat === 'envoi' || action.etat === 'pdf' ? undefined : b.onClick}
                style={{
                  height: 46,
                  borderRadius: 11,
                  border: b.primaire ? 'none' : '1.5px solid ' + C.border,
                  background: b.primaire ? C.teal : '#fff',
                  color: b.primaire ? '#fff' : C.ink,
                  fontSize: 13,
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 16 }}>{b.icone}</span>
                {b.label}
              </button>
            ))}
          </div>
          {action.message && (
            <div
              role="status"
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                marginTop: 8,
                color: action.etat === 'echec' ? C.red : C.green,
              }}
            >
              {action.etat === 'echec' ? '✕ ' : '✓ '}
              {action.message}
            </div>
          )}
        </div>
        {feuilleEnvoi && (
          <div
            className="dchnp"
            onClick={() => setFeuilleEnvoi(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 60,
              background: 'rgba(15,26,46,.55)',
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <div
              role="dialog"
              aria-label="Envoyer la décharge"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                width: '100%',
                borderRadius: '16px 16px 0 0',
                padding: '16px 14px calc(14px + env(safe-area-inset-bottom))',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 10 }}>
                Envoyer la décharge
              </div>
              {!dest?.email && (
                <input
                  type="email"
                  value={emailEnvoi}
                  onChange={(e) => setEmailEnvoi(e.target.value)}
                  placeholder="E-mail du client"
                  aria-label="E-mail du client"
                  style={{
                    width: '100%',
                    border: '1px solid ' + C.border,
                    borderRadius: 10,
                    padding: '11px 12px',
                    fontSize: 14,
                    marginBottom: 8,
                    boxSizing: 'border-box',
                  }}
                />
              )}
              {[
                {
                  cle: 'email',
                  icone: '📨',
                  titre: 'Par e-mail, PDF joint',
                  sous:
                    (boite ? 'De ' + boite.adresse + ' · ' : '') +
                    (dest?.email || emailValide
                      ? 'à ' + (dest?.email || emailEnvoi.trim())
                      : 'Indique l’e-mail du client'),
                  onClick: envoieParEmail,
                  inactif: !dest?.email && !emailValide,
                },
                ...(telWhatsApp
                  ? [
                      {
                        cle: 'whatsapp',
                        icone: '💬',
                        titre: 'WhatsApp',
                        sous: dest?.tel,
                        href:
                          'https://wa.me/' + telWhatsApp + '?text=' + encodeURIComponent(msgEnvoi),
                      },
                    ]
                  : []),
              ].map((o) => {
                const contenu = (
                  <>
                    <span style={{ fontSize: 22 }}>{o.icone}</span>
                    <div style={{ minWidth: 0, textAlign: 'left' }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{o.titre}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{o.sous}</div>
                    </div>
                  </>
                );
                const style = {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '12px 12px',
                  marginBottom: 8,
                  borderRadius: 12,
                  border: '1.5px solid ' + (o.inactif ? C.border : C.teal),
                  background: o.inactif ? C.navyXL : C.tealL,
                  cursor: o.inactif ? 'default' : 'pointer',
                  textDecoration: 'none',
                  opacity: o.inactif ? 0.75 : 1,
                };
                return o.href ? (
                  <a
                    key={o.cle}
                    href={o.href}
                    target="_blank"
                    rel="noreferrer"
                    style={style}
                    onClick={() => {
                      setFeuilleEnvoi(false);
                      // Partagée par WhatsApp, la décharge s'enregistre aussi :
                      // au classeur, et le patron est prévenu.
                      enregistreDecharge().catch(() => {});
                    }}
                  >
                    {contenu}
                  </a>
                ) : (
                  <button
                    key={o.cle}
                    type="button"
                    style={style}
                    onClick={o.inactif ? undefined : o.onClick}
                  >
                    {contenu}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setFeuilleEnvoi(false)}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.muted,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 10px' }}>
          <div
            className="dch"
            style={{
              background: '#fff',
              borderRadius: 4,
              padding: '24px 22px',
              maxWidth: 680,
              margin: '0 auto',
              boxShadow: '0 2px 20px rgba(0,0,0,.2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderBottom: '2px solid ' + C.ink,
                paddingBottom: 12,
                marginBottom: 16,
              }}
            >
              <div>
                {soc?.logo && <img src={soc.logo} style={{ height: 30, marginBottom: 6 }} alt="" />}
                <div style={{ fontSize: 14, fontWeight: 800 }}>{soc?.nom || SOC.nom}</div>
                <div style={{ fontSize: 8, color: C.grey }}>
                  {soc?.adr || SOC.adr} · {soc?.npa || SOC.npa}
                </div>
                {(soc?.tel || soc?.email) && (
                  <div style={{ fontSize: 8, color: C.grey }}>
                    {[soc?.tel, soc?.email].filter(Boolean).join(' · ')}
                  </div>
                )}
                {soc?.tva && <div style={{ fontSize: 8, color: C.muted }}>N° TVA : {soc.tva}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 24, fontWeight: 900, color: DA, letterSpacing: -0.5 }}>
                  DÉCHARGE
                </div>
                <div style={{ fontSize: 8, color: C.muted }}>{today()}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    fontSize: 7.5,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: C.muted,
                    marginBottom: 3,
                  }}
                >
                  Client
                </div>
                <div style={{ fontSize: 10, fontWeight: 700 }}>{dest?.nom || '—'}</div>
                <div style={{ fontSize: 9, color: C.grey }}>
                  {[dest?.adr, dest?.npa].filter(Boolean).join(', ')}
                </div>
                {(dest?.tel || dest?.email) && (
                  <div style={{ fontSize: 9, color: C.grey }}>
                    {[dest?.tel, dest?.email].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    fontSize: 7.5,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: C.muted,
                    marginBottom: 3,
                  }}
                >
                  {estMag ? 'Lieu du rendez-vous' : 'Lieu des travaux'}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700 }}>
                  {f.lieu || [dest?.adr, dest?.npa].filter(Boolean).join(', ') || '—'}
                </div>
                {!f.lieu && (
                  <div style={{ fontSize: 7.5, color: C.muted }}>
                    (identique à l'adresse du client)
                  </div>
                )}
              </div>
            </div>
            {f.objet && (
              <div
                style={{
                  background: C.navyXL,
                  borderLeft: '3px solid ' + DA,
                  padding: '10px 12px',
                  fontSize: 10,
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                Objet : {f.objet}
              </div>
            )}
            <div style={{ fontSize: 10.5, lineHeight: 1.9, color: C.ink, marginBottom: 24 }}>
              {f.texte}
            </div>
            {photos.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 7.5,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: C.muted,
                    marginBottom: 6,
                  }}
                >
                  Photos annexées
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {photos.map((p, i) => (
                    <img
                      key={p.cle}
                      src={p.apercu}
                      alt={`Photo annexée ${i + 1}`}
                      style={{
                        width: '31.5%',
                        borderRadius: 5,
                        border: '1px solid ' + C.border,
                        objectFit: 'cover',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 30 }}>
              <div style={{ width: '45%' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  {/* Le tampon, si Réglages → Documents le veut sur les décharges. */}
                  {tamponVoulu(soc, 'decharge') && soc?.tampon && (
                    <img
                      src={soc.tampon}
                      alt="tampon"
                      style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {sigSoc && estUneImage(sigSoc) ? (
                      <img src={sigSoc} alt="" style={{ height: 50, maxWidth: '100%' }} />
                    ) : sigSoc ? (
                      <svg viewBox="0 0 320 70" style={{ width: '100%', height: 50 }}>
                        <path d={sigSoc} fill="none" stroke={C.ink} strokeWidth="2" />
                      </svg>
                    ) : (
                      <div style={{ height: 50 }} />
                    )}
                  </div>
                </div>
                <div
                  style={{
                    borderTop: '1px solid ' + C.ink,
                    paddingTop: 5,
                    fontSize: 8.5,
                    color: C.muted,
                  }}
                >
                  L'entreprise (pour accord)
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, marginTop: 3 }}>
                  {soc?.nom || SOC.nom}
                </div>
                {signataireBureau && <div style={{ fontSize: 8.5 }}>{signataireBureau}</div>}
                <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>Date : {today()}</div>
              </div>
              <div style={{ width: '45%' }}>
                {sig ? (
                  <svg viewBox="0 0 320 70" style={{ width: '100%', height: 50 }}>
                    <path d={sig} fill="none" stroke={C.ink} strokeWidth="2" />
                  </svg>
                ) : (
                  <div style={{ height: 50 }} />
                )}
                <div
                  style={{
                    borderTop: '1px solid ' + C.ink,
                    paddingTop: 5,
                    fontSize: 8.5,
                    color: C.muted,
                  }}
                >
                  Le client (lu et approuvé)
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, marginTop: 3 }}>{dest?.nom}</div>
                <div style={{ fontSize: 8, color: C.muted, marginTop: 2 }}>Date : {today()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="📜 Décharge de responsabilité"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <Card style={{ marginBottom: 11 }}>
        <Bar icon="📜" title="Établir une décharge" color={C.navy} />
        <div style={{ padding: 13 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[
              [false, 'Client existant'],
              [true, 'Saisir à la main'],
            ].map(([v, lb]) => {
              const on = !!f.destManuel === v;
              return (
                <div
                  key={String(v)}
                  onClick={() => setF((p) => ({ ...p, destManuel: v }))}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '9px',
                    borderRadius: 9,
                    fontSize: 10.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: on ? C.navy : C.white,
                    color: on ? '#fff' : C.grey,
                    border: '1px solid ' + C.border,
                  }}
                >
                  {lb}
                </div>
              );
            })}
          </div>
          {!f.destManuel && clients.length > 0 && (
            <Inp label="Client">
              <select
                value={f.clientId}
                onChange={(e) => setF((p) => ({ ...p, clientId: +e.target.value }))}
                style={{
                  width: '100%',
                  border: '1px solid ' + C.border,
                  borderRadius: 9,
                  padding: '10px',
                  fontSize: 11,
                }}
              >
                {ordreDeSaisie(clients).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
            </Inp>
          )}
          {!f.destManuel && clients.length === 0 && (
            <div
              style={{
                fontSize: 9.5,
                color: C.muted,
                background: C.navyXL,
                borderRadius: 8,
                padding: '9px 11px',
                marginBottom: 10,
              }}
            >
              Aucun client enregistré — utilise « Saisir à la main » pour indiquer le destinataire.
            </div>
          )}
          {f.destManuel && (
            <>
              <BoutonContacts sm ghost color={C.teal} onContact={remplitDestinataire} />
              {collaborateurs.length > 0 && (
                <div style={{ margin: '9px 0' }}>
                  <label
                    htmlFor="decharge-collaborateur"
                    style={{
                      display: 'block',
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      marginBottom: 6,
                      textTransform: 'uppercase',
                      letterSpacing: '.3px',
                    }}
                  >
                    👷 Ou choisir un collaborateur
                  </label>
                  <select
                    id="decharge-collaborateur"
                    value=""
                    onChange={(e) => {
                      const emp = collaborateurs.find((x) => String(x.id) === e.target.value);
                      if (emp) remplitDestinataire(coordonneesDuCollaborateur(emp));
                    }}
                    style={{
                      width: '100%',
                      border: '1.5px solid ' + C.border,
                      borderRadius: 10,
                      padding: '11px',
                      fontSize: 14,
                      fontFamily: 'inherit',
                      background: '#fafbfd',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">— Sélectionner un collaborateur —</option>
                    {collaborateurs.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {coordonneesDuCollaborateur(emp).nom || 'Collaborateur'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <Inp
                label="Destinataire (nom)"
                value={f.destNom}
                onChange={(e) => setF((p) => ({ ...p, destNom: e.target.value }))}
                placeholder="Nom et prénom ou société"
              />
              <Inp
                label="Adresse"
                value={f.destAdr}
                onChange={(e) => setF((p) => ({ ...p, destAdr: e.target.value }))}
                placeholder="Rue et numéro"
              />
              <Inp
                label="NPA / Localité"
                value={f.destNpa}
                onChange={(e) => setF((p) => ({ ...p, destNpa: e.target.value }))}
                placeholder="1200 Genève"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Inp
                    label="Téléphone"
                    value={f.destTel}
                    onChange={(e) => setF((p) => ({ ...p, destTel: e.target.value }))}
                    placeholder="+41 …"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Inp
                    label="E-mail"
                    value={f.destEmail}
                    onChange={(e) => setF((p) => ({ ...p, destEmail: e.target.value }))}
                    placeholder="client@mail.ch"
                  />
                </div>
              </div>
            </>
          )}
          <Inp
            label="Objet"
            value={f.objet}
            onChange={(e) => setF((p) => ({ ...p, objet: e.target.value }))}
            placeholder="Ex : intervention sur installation existante non conforme"
          />
          <Inp
            label={
              (estMag ? 'Lieu du rendez-vous' : 'Lieu des travaux') +
              " (si différent de l'adresse du client)"
            }
            value={f.lieu}
            onChange={(e) => setF((p) => ({ ...p, lieu: e.target.value }))}
            // Saisie à la main : l'adresse d'un client de la liste n'a rien à
            // faire ici.
            placeholder={
              !f.destManuel && cl?.adr
                ? cl.adr + ', ' + (cl.npa || '')
                : estMag
                  ? 'Adresse du rendez-vous'
                  : 'Adresse du chantier / du bien'
            }
          />
          <Inp
            label="Texte de la décharge"
            rows={5}
            value={f.texte}
            onChange={(e) => setF((p) => ({ ...p, texte: e.target.value }))}
          />
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: C.muted,
                marginBottom: 7,
                textTransform: 'uppercase',
                letterSpacing: '.3px',
              }}
            >
              📷 Photos (état des lieux, preuve…)
            </div>
            <PhotosDecharge photos={photos} onChange={setPhotos} figees={photosFigees} />
          </div>
        </div>
      </Card>
      <Card style={{ marginBottom: 11 }}>
        <Bar
          icon="✍"
          title="Signatures"
          color={C.navy}
          sub="Les deux parties signent ; ajoutées sur le document A4"
        />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, marginBottom: 5 }}>
            Signature de l'entreprise
          </div>
          {/* « Ramener la signature » : celle que le patron, la secrétaire ou
              le technicien a enregistrée, au lieu de la redessiner. */}
          {estUneImage(sigSoc) ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img
                src={sigSoc}
                alt="Signature de l'entreprise"
                style={{
                  maxHeight: 60,
                  maxWidth: '60%',
                  background: '#fff',
                  border: '1px solid ' + C.border,
                  borderRadius: 8,
                }}
              />
              <button
                type="button"
                onClick={() => setSigSoc('')}
                style={{
                  border: '1px solid ' + C.red,
                  background: '#fff',
                  color: C.red,
                  borderRadius: 8,
                  padding: '7px 10px',
                  fontSize: 10,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Retirer
              </button>
            </div>
          ) : (
            <>
              <BoutonMaSignature soc={soc} onUtilise={setSigSoc} />
              <SignPad value={sigSoc} onChange={setSigSoc} color={C.navy} />
            </>
          )}
          <div style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, margin: '12px 0 5px' }}>
            Signature du client
          </div>
          <SignPad value={sig} onChange={setSig} color={C.navy} />
        </div>
      </Card>
      <Btn label="👁 Aperçu de la décharge A4" color={DA} full onClick={() => setView('doc')} />
      {(() => {
        const sansObjet = !f.objet.trim();
        const sansDest = !(dest?.nom || '').trim();
        return sansObjet || sansDest ? (
          <div
            style={{
              fontSize: 8.5,
              color: C.amber,
              textAlign: 'center',
              marginTop: 7,
              fontWeight: 600,
            }}
          >
            {sansObjet && sansDest
              ? "Conseil : renseigne l'objet et le destinataire pour une décharge complète (l'aperçu reste consultable)."
              : sansObjet
                ? "Conseil : renseigne l'objet pour une décharge complète (l'aperçu reste consultable)."
                : "Conseil : renseigne le destinataire pour une décharge complète (l'aperçu reste consultable)."}
          </div>
        ) : (
          <div
            style={{
              fontSize: 8.5,
              color: C.green,
              textAlign: 'center',
              marginTop: 7,
              fontWeight: 700,
            }}
          >
            ✓ Décharge complète — prête à signer et envoyer.
          </div>
        );
      })()}
    </Shell>
  );
}
